import { SkyDialog, SkyMessageBar, SkyTextarea } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import { FORCE_RESET_REASON_MIN_LENGTH } from "@shared/types/runs";
import { InlineErrorBar, type InlineFailure } from "@/lib/feedback/InlineErrorBar";

/**
 * Force-reset a stuck run (FR-54).
 *
 * The reason is required here AND on the server (the same minimum, B-40), and
 * the two refusals are deliberately different in kind: this one stops the request being sent,
 * and the server's 422 (none) or 400 (too short) stops a caller that is not this screen. Neither is the
 * control on its own. The reason is the only record of why someone overrode
 * the system's own view of a run, and it is written to the audit trail against
 * the acting account, which the dialog says before the button is pressed
 * rather than after.
 *
 * Session 10 (ux-s10 §3): the dialog closes only when the parent says the reset landed. A
 * refusal is shown inside it (`error`), with the reason still typed, rather than in a notice
 * behind the modal; SkyDialog's own close after the primary action is declined for that.
 */

const MIN_REASON_LENGTH = FORCE_RESET_REASON_MIN_LENGTH;

export interface ForceResetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  runId: string;
  isPending: boolean;
  onConfirm: (reason: string) => void;
  /** The last refusal, shown inside the dialog; null or absent when there is none. */
  error?: InlineFailure | null;
}

export function ForceResetDialog({
  open,
  onOpenChange,
  runId,
  isPending,
  onConfirm,
  error = null,
}: ForceResetDialogProps) {
  // Mounted by its parent only while it is open, so the fields start empty
  // every time without an effect clearing them after the fact. A reason left
  // over from a previous force reset is exactly the wrong thing to submit.
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  const trimmed = reason.trim();
  const tooShort = trimmed.length < MIN_REASON_LENGTH;

  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Force reset run ${runId}?`}
      size="medium"
      testId="force-reset-dialog"
      // The parent closes it on success; a failure must stay on screen to be read.
      onBeforeClose={(reason) => reason !== "primaryAction"}
      primaryAction={{
        label: "Force reset",
        destructive: true,
        loading: isPending,
        disabled: isPending || tooShort,
        onClick: () => {
          setTouched(true);
          if (tooShort) return;
          onConfirm(trimmed);
        },
      }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false) }}
    >
      <div className="flex flex-col gap-3">
        <SkyMessageBar
          intent="error"
          layout="multiline"
          title="This stops the run without waiting for the job"
          message="Use it only when the job is gone. Any job still running is not stopped by this, so the run cannot be retried afterwards. Start a new run instead. The reason you give is recorded in the audit trail against your account."
        />
        <SkyTextarea
          label="Reason"
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          onBlur={() => setTouched(true)}
          rows={3}
          maxLength={500}
          showCount
          hint={`At least ${MIN_REASON_LENGTH} characters. Say what makes this run unrecoverable.`}
          validationState={touched && tooShort ? "error" : "none"}
          validationMessage={
            touched && tooShort
              ? `A force reset needs a reason of at least ${MIN_REASON_LENGTH} characters, which is recorded in the audit trail.`
              : undefined
          }
          testId="force-reset-reason"
        />
        <InlineErrorBar
          error={error}
          title={error?.retryable ? undefined : "The run was not force reset"}
          testId="force-reset-error"
        />
      </div>
    </SkyDialog>
  );
}
